<template>
  <div id="app">
    <b-loading :active="$auth.loading"></b-loading>
    <AppLayout v-if="!$auth.loading">
      <keep-alive>
        <router-view></router-view>
      </keep-alive>
    </AppLayout>
  </div>
</template>
<script>
import AppLayout from '@/components/AppLayout.vue';
import { LogFactory } from '@/helpers/debug';

const log = LogFactory('App:Intercom', false);

export default {
  components: {
    AppLayout
  },
  data() {
    return {
      intercomBootAttempted: false,
      intercomBooting: false
    };
  },
  watch: {
    // Upgrade to an identity-verified Intercom session once the user logs in,
    // and tear it down (back to anonymous) on logout.
    '$auth.user': {
      immediate: true,
      handler(user) {
        if (user) {
          this.bootIntercomIdentified(user);
        } else if (this.intercomBootAttempted || this.intercomBooting) {
          // Shut down even if an identified boot is still in flight - the plugin
          // cancels the pending boot so a logged-out user is never identified.
          this.$intercom.shutdown();
          this.intercomBootAttempted = false;
          this.intercomBooting = false;
          this.bootIntercomAnonymous();
        }
      }
    }
  },
  mounted() {
    // Boot Intercom anonymously so the messenger is available to all visitors
    // (the "Get Help" / "Contact us" entry points) before they authenticate.
    this.bootIntercomAnonymous();
  },
  methods: {
    bootIntercomAnonymous() {
      if (!process.env.VUE_APP_INTERCOM_ID) {
        return;
      }
      this.$intercom
        .boot({
          app_id: process.env.VUE_APP_INTERCOM_ID,
          api_base: process.env.VUE_APP_INTERCOM_API_BASE
        })
        .catch((error) => {
          log('Anonymous boot failed', error);
        });
    },
    async bootIntercomIdentified(user) {
      if (this.intercomBootAttempted || this.intercomBooting || !process.env.VUE_APP_INTERCOM_ID) {
        return;
      }
      const intercomJwt = user[process.env.VUE_APP_AUTH0_INTERCOM_CLAIM];
      const userId = user[process.env.VUE_APP_AUTH0_USERNAME_CLAIM];

      if (!userId || !intercomJwt) {
        log('Not booted - missing required claim(s)', {
          hasUserId: !!userId,
          hasIntercomJwt: !!intercomJwt
        });
        return;
      }

      this.intercomBooting = true;
      try {
        await this.$intercom.boot({
          app_id: process.env.VUE_APP_INTERCOM_ID,
          api_base: process.env.VUE_APP_INTERCOM_API_BASE,
          intercom_user_jwt: intercomJwt,
          user_id: userId,
          name: user.name,
          email: user.email
        });
        this.intercomBootAttempted = true;
      } catch (error) {
        log('Identified boot failed', error);
        this.intercomBootAttempted = false;
      } finally {
        this.intercomBooting = false;
      }
    }
  }
};
</script>
<style lang="scss">
@import './assets/style/_main.scss';
</style>
